/**
 * Step Navigation Hook
 * Manages step progression logic for the Athena TARA workflow
 */

import { useState, useCallback, useMemo } from 'react';
import { STEP_CONFIGURATION } from '../../../config/athenaWorkflowConfig';
import { getAgentName } from '../../../config/agentMapping';

export const useStepNavigation = (initialStep = 0) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [blockedSteps, setBlockedSteps] = useState({});
  const [stepValidations, setStepValidations] = useState({});
  const [navigationHistory, setNavigationHistory] = useState([initialStep]);

  /**
   * Get total number of steps
   */
  const totalSteps = useMemo(() => {
    return Object.keys(STEP_CONFIGURATION).length;
  }, []);

  /**
   * Check if a step is completed
   */
  const isStepCompleted = useCallback((step) => {
    return completedSteps.includes(step);
  }, [completedSteps]);

  /**
   * Check if a step is validated (approved by user)
   */
  const isStepValidated = useCallback((step) => {
    return stepValidations[step]?.approved === true;
  }, [stepValidations]);

  /**
   * Check if a step is blocked
   */
  const isStepBlocked = useCallback((step) => {
    return blockedSteps[step] !== undefined;
  }, [blockedSteps]);

  /**
   * Get explicit status for a step
   */
  const getStepStatus = useCallback((step) => {
    if (isStepBlocked(step)) return 'blocked';
    if (isStepCompleted(step)) return 'completed';
    if (step === currentStep) return 'in_progress';
    return 'pending';
  }, [currentStep, isStepCompleted, isStepBlocked]);

  /**
   * Check if navigation to a specific step is allowed
   */
  const canNavigateToStep = useCallback((targetStep) => {
    // Can always navigate to step 0 (idle)
    if (targetStep === 0) return true;

    // Cannot navigate beyond total steps
    if (targetStep < 1 || targetStep > totalSteps) return false;

    // Can navigate to current step
    if (targetStep === currentStep) return true;

    // Can navigate to previously completed steps
    if (isStepCompleted(targetStep)) return true;

    // Can navigate to the next step if current step is validated
    if (targetStep === currentStep + 1) {
      return currentStep === 0 || isStepValidated(currentStep);
    }

    // Cannot skip steps
    return false;
  }, [currentStep, totalSteps, isStepCompleted, isStepValidated]);

  /**
   * Get the maximum allowed step (furthest the user can navigate)
   */
  const getMaxAllowedStep = useCallback(() => {
    if (completedSteps.length === 0) {
      // If no steps completed, can go to step 1 from step 0
      return currentStep === 0 ? 1 : currentStep;
    }

    // Can go to the highest completed step + 1 (if current step is validated)
    const maxCompleted = Math.max(...completedSteps);

    // If current step is validated and is the max completed, can go one step further
    if (currentStep === maxCompleted && isStepValidated(currentStep)) {
      return Math.min(maxCompleted + 1, totalSteps);
    }

    return Math.max(maxCompleted, currentStep);
  }, [currentStep, completedSteps, totalSteps, isStepValidated]);

  /**
   * Navigate to a specific step
   */
  const navigateToStep = useCallback((targetStep, force = false) => {
    // Validate navigation
    if (!force && !canNavigateToStep(targetStep)) {
      console.warn(`Navigation to step ${targetStep} not allowed from step ${currentStep}`);
      return {
        success: false,
        reason: `Cannot navigate to step ${targetStep}`,
        currentStep,
        targetStep
      };
    }

    // Update current step
    const previousStep = currentStep;
    setCurrentStep(targetStep);

    // Add to navigation history
    setNavigationHistory(prev => [...prev, targetStep]);

    console.log(`Step navigation: ${previousStep} → ${targetStep}`);

    return {
      success: true,
      previousStep,
      currentStep: targetStep,
      direction: targetStep > previousStep ? 'forward' : 'backward'
    };
  }, [currentStep, canNavigateToStep]);

  /**
   * Move to the next step
   */
  const nextStep = useCallback(() => {
    const next = currentStep + 1;

    if (next > totalSteps) {
      console.warn('Already at final step');
      return { success: false, reason: 'Already at final step' };
    }

    return navigateToStep(next);
  }, [currentStep, totalSteps, navigateToStep]);

  /**
   * Move to the previous step
   */
  const previousStep = useCallback(() => {
    const prev = currentStep - 1;

    if (prev < 0) {
      console.warn('Already at first step');
      return { success: false, reason: 'Already at first step' };
    }

    return navigateToStep(prev);
  }, [currentStep, navigateToStep]);

  /**
   * Mark a step as completed
   */
  const markStepCompleted = useCallback((step) => {
    if (!completedSteps.includes(step)) {
      setCompletedSteps(prev => {
        const updated = [...prev, step];
        // Keep sorted and unique
        return [...new Set(updated)].sort((a, b) => a - b);
      });

      // Clear any block status when completing
      setBlockedSteps(prev => {
        const updated = { ...prev };
        delete updated[step];
        return updated;
      });

      console.log(`Step ${step} marked as completed`);
      return true;
    }

    return false;
  }, [completedSteps]);

  /**
   * Block a step due to error
   */
  const blockStep = useCallback((step, errorMessage) => {
    setBlockedSteps(prev => ({
      ...prev,
      [step]: {
        errorMessage,
        blockedAt: new Date().toISOString(),
        timestamp: Date.now()
      }
    }));

    console.log(`Step ${step} blocked:`, errorMessage);
  }, []);

  /**
   * Unblock a step
   */
  const unblockStep = useCallback((step) => {
    setBlockedSteps(prev => {
      const updated = { ...prev };
      delete updated[step];
      return updated;
    });

    console.log(`Step ${step} unblocked`);
  }, []);

  /**
   * Mark current step as completed
   */
  const completeCurrentStep = useCallback(() => {
    return markStepCompleted(currentStep);
  }, [currentStep, markStepCompleted]);

  /**
   * Update validation status for a step
   */
  const updateStepValidation = useCallback((step, validation) => {
    setStepValidations(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        ...validation,
        timestamp: Date.now()
      }
    }));

    console.log(`Step ${step} validation updated:`, validation);
  }, []);

  /**
   * Validate current step
   */
  const validateCurrentStep = useCallback((approved, feedback = null) => {
    updateStepValidation(currentStep, {
      approved,
      feedback,
      validatedAt: new Date().toISOString()
    });

    // If approved, mark as completed
    if (approved) {
      completeCurrentStep();
    }

    return approved;
  }, [currentStep, updateStepValidation, completeCurrentStep]);

  /**
   * Get step information
   */
  const getStepInfo = useCallback((step = null) => {
    const targetStep = step !== null ? step : currentStep;
    const config = STEP_CONFIGURATION[targetStep];

    if (!config) {
      return null;
    }

    const status = getStepStatus(targetStep);

    return {
      step: targetStep,
      name: config.name,
      status,
      agentName: getAgentName(targetStep, 'primary'),
      reviewAgentName: getAgentName(targetStep, 'review'),
      isCompleted: isStepCompleted(targetStep),
      isValidated: isStepValidated(targetStep),
      isCurrent: targetStep === currentStep,
      isBlocked: isStepBlocked(targetStep),
      canNavigate: canNavigateToStep(targetStep),
      validation: stepValidations[targetStep] || null,
      blockInfo: blockedSteps[targetStep] || null,
      config
    };
  }, [currentStep, isStepCompleted, isStepValidated, isStepBlocked, canNavigateToStep, stepValidations, blockedSteps, getStepStatus]);

  /**
   * Get all steps information
   */
  const getAllStepsInfo = useCallback(() => {
    return Array.from({ length: totalSteps }, (_, i) => i + 1)
      .map(step => getStepInfo(step));
  }, [totalSteps, getStepInfo]);

  /**
   * Get progress percentage
   */
  const getProgress = useCallback(() => {
    if (totalSteps === 0) return 0;
    return Math.round((completedSteps.length / totalSteps) * 100);
  }, [completedSteps.length, totalSteps]);

  /**
   * Reset navigation state
   */
  const reset = useCallback(() => {
    setCurrentStep(0);
    setCompletedSteps([]);
    setBlockedSteps({});
    setStepValidations({});
    setNavigationHistory([0]);
    console.log('Step navigation reset');
  }, []);

  /**
   * Restore navigation state from saved data
   */
  const restoreState = useCallback((savedState) => {
    if (savedState) {
      setCurrentStep(savedState.currentStep || 0);
      setCompletedSteps(savedState.completedSteps || []);
      setBlockedSteps(savedState.blockedSteps || {});
      setStepValidations(savedState.stepValidations || {});
      setNavigationHistory(savedState.navigationHistory || [savedState.currentStep || 0]);
      console.log('Step navigation state restored', savedState);
    }
  }, []);

  /**
   * Get current state for persistence
   */
  const getState = useCallback(() => {
    return {
      currentStep,
      completedSteps,
      blockedSteps,
      stepValidations,
      navigationHistory
    };
  }, [currentStep, completedSteps, blockedSteps, stepValidations, navigationHistory]);

  /**
   * Get step message for UI display
   */
  const getStepMessage = useCallback((step = null) => {
    const targetStep = step !== null ? step : currentStep;
    const info = getStepInfo(targetStep);

    if (!info) {
      return 'Invalid step';
    }

    if (info.isCompleted && info.isValidated) {
      return `${info.name} - Completed`;
    }

    if (info.isCompleted && !info.isValidated) {
      return `${info.name} - Review Required`;
    }

    if (info.isCurrent) {
      return `${info.name} - In Progress`;
    }

    if (!info.canNavigate) {
      return `${info.name} - Complete previous steps first`;
    }

    return info.name;
  }, [currentStep, getStepInfo]);

  return {
    // State
    currentStep,
    completedSteps,
    blockedSteps,
    stepValidations,
    navigationHistory,
    totalSteps,

    // Navigation
    navigateToStep,
    nextStep,
    previousStep,
    canNavigateToStep,
    getMaxAllowedStep,

    // Step management
    markStepCompleted,
    completeCurrentStep,
    updateStepValidation,
    validateCurrentStep,
    blockStep,
    unblockStep,

    // Step info
    getStepInfo,
    getAllStepsInfo,
    getStepMessage,
    getStepStatus,
    isStepCompleted,
    isStepValidated,
    isStepBlocked,

    // Progress
    getProgress,

    // State management
    reset,
    restoreState,
    getState
  };
};